/**
 * Read-only sheet showing check details for Finalized or Cancelled checks.
 * Slides in from the right; same structure as Check Received form but no editing.
 */

import { formatPaymentPeriodUsRangeLabel } from "@/components/license-details/cards/license-payments-derived";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ConfirmDeleteDialog } from "@/components/ui/confirm-delete-dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "@/components/ui/sonner";
import { UserType } from "@/constants";
import { client } from "@/hera-api/client/client.gen";
import {
  type ManualCheckPaymentDetail,
  type ManualCheckPaymentLicenseDetail,
  getManualCheckPaymentDetail,
  getRegistrantPaymentProcessorDetail,
} from "@/hera-api/manual-check-payments-api";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { downloadInvoicePdfFromApi } from "@/lib/download-invoice-pdf";
import { pickManualCheckReceiptAnchor } from "@/lib/manual-check-receipt";
import {
  type ManualPaymentPrintVariant,
  downloadManualPaymentDetail,
  formatPaymentDocumentUsDate,
  printManualPaymentDetail,
} from "@/lib/manual-payment-detail-print";
import { receiptScopeAccess } from "@/lib/receipt-scope";
import {
  downloadFileFromResponse,
  formatCurrency,
  getQueryErrorMessage,
  getUserType,
} from "@/lib/utils";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Download, FileText, Loader2, Printer, XCircle } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

export function navigateToLicenseFromPaymentDetail(
  navigate: ReturnType<typeof useNavigate>,
  userType: UserType | null | undefined,
  licenseId: string,
): void {
  if (userType === UserType.GOVERNMENT) {
    void navigate({
      to: "/government/registrations",
      search: { licenseId },
    });
    return;
  }
  if (userType === UserType.OPERATOR) {
    void navigate({
      to: "/operator/registrations",
      search: { licenseId },
    });
    return;
  }
  void navigate({
    to: "/registrant/licenses/$licenseId/view",
    params: { licenseId },
  });
}

function parseNum(s: string): number {
  const n = Number.parseFloat(s);
  return Number.isNaN(n) ? 0 : n;
}

export interface CheckReceivedViewSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  manualTransactionId: string | null;
  /** Card/ACH checkout (registrant portal); mutually exclusive with manual check id. */
  paymentProcessorTransactionId?: string | null;
  /** When provided (e.g. registrant manual check), use this instead of operator API. */
  getManualDetail?: (id: string) => Promise<ManualCheckPaymentDetail>;
  /** Override processor detail fetch (defaults to registrant endpoint). */
  getProcessorDetail?: (id: string) => Promise<ManualCheckPaymentDetail>;
  /** Document title and ID column label in the print view. */
  printVariant?: ManualPaymentPrintVariant;
  /** Registrant portal checks: header uses Download (HTML) instead of Print. Invoice variant always uses download in the header. */
  registrantPortalDocumentDownload?: boolean;
  /** Registrant portal: show Cancel when the open row is still cancellable (awaiting check / unpaid invoice). */
  registrantCancelEnabled?: boolean;
  onRegistrantCancel?: () => void;
  registrantCancelPending?: boolean;
}

export function CheckReceivedViewSheet({
  isOpen,
  onOpenChange,
  manualTransactionId,
  paymentProcessorTransactionId = null,
  getManualDetail,
  getProcessorDetail,
  printVariant = "check",
  registrantPortalDocumentDownload = false,
  registrantCancelEnabled = false,
  onRegistrantCancel,
  registrantCancelPending = false,
}: CheckReceivedViewSheetProps) {
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const { user } = useAuth0();
  const userType = getUserType(user);
  const navigate = useNavigate();
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const activeManualId = manualTransactionId;
  const activeProcessorId = paymentProcessorTransactionId;
  const effectivePrintVariant: ManualPaymentPrintVariant = activeProcessorId
    ? "processor"
    : printVariant;
  const headerUsesDocumentDownload =
    registrantPortalDocumentDownload || printVariant === "invoice";
  const {
    data: detail,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "manual-check-payment-detail-view",
      activeManualId,
      activeProcessorId,
      getManualDetail ? "registrant-manual" : "operator",
    ],
    queryFn: async () => {
      if (activeManualId) {
        return (getManualDetail ?? getManualCheckPaymentDetail)(activeManualId);
      }
      if (activeProcessorId) {
        return (getProcessorDetail ?? getRegistrantPaymentProcessorDetail)(
          activeProcessorId,
        );
      }
      throw new Error("No payment selected");
    },
    enabled: isOpen && !!(activeManualId ?? activeProcessorId),
  });

  const receiptAnchor = useMemo(
    () => (detail ? pickManualCheckReceiptAnchor(detail) : null),
    [detail],
  );

  const showReceiptForFinalisedCheck =
    (detail?.status === "Finalized" || detail?.status === "Succeeded") &&
    receiptAnchor !== null;

  const { data: receiptTemplateGlobal, isLoading: isReceiptTemplateLoading } =
    useQuery({
      queryKey: ["paymentReceiptTemplateAvailable", receiptAnchor?.licenseId],
      queryFn: async () => {
        const baseUrl = (client.getConfig().baseUrl || "").replace(/\/$/, "");
        const auth = client.getConfig().auth;
        const url = `${baseUrl}/shared/licenses/${receiptAnchor?.licenseId}/payment-receipt-template-available`;
        const doFetch =
          client.getConfig().fetch ?? globalThis.fetch.bind(globalThis);
        const res = await doFetch(
          new Request(url, {
            headers:
              typeof auth === "string" && auth
                ? { Authorization: `Bearer ${auth}` }
                : {},
          }),
        );
        if (!res.ok) {
          throw new Error(await res.text());
        }
        return res.json() as Promise<{ available: boolean }>;
      },
      enabled:
        isOpen &&
        !!(activeManualId ?? activeProcessorId) &&
        isAuthSetupComplete &&
        showReceiptForFinalisedCheck &&
        !!receiptAnchor?.licenseId,
    });

  const hasPaymentReceiptTemplate = receiptTemplateGlobal?.available ?? false;

  const [receiptDownloading, setReceiptDownloading] = useState(false);
  const [documentHeaderDownloading, setDocumentHeaderDownloading] =
    useState(false);

  const handleLicenseClick = useCallback(
    (licenseId: string) => {
      onOpenChange(false);
      navigateToLicenseFromPaymentDetail(navigate, userType, licenseId);
    },
    [navigate, onOpenChange, userType],
  );

  const headerDownloadGeneratingLabel = useMemo(() => {
    if (effectivePrintVariant === "invoice") {
      return "Generating Invoice";
    }
    if (effectivePrintVariant === "processor") {
      return "Generating Payment";
    }
    return "Generating Check";
  }, [effectivePrintVariant]);

  const handleDownloadDetailHeader = useCallback(async () => {
    if (!detail) {
      return;
    }
    setDocumentHeaderDownloading(true);
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve());
      });
    });
    try {
      if (effectivePrintVariant === "invoice") {
        const mtId = detail.manual_transaction_id;
        if (!mtId) {
          toast({
            title: "Could not download invoice",
            description: "Missing invoice transaction id.",
            variant: "destructive",
          });
          return;
        }
        await downloadInvoicePdfFromApi(
          mtId,
          registrantPortalDocumentDownload ? "registrant" : "operator",
        );
      } else {
        downloadManualPaymentDetail(detail, {
          variant: effectivePrintVariant,
        });
      }
    } catch (err) {
      if (effectivePrintVariant === "invoice") {
        toast({
          title: "Could not download invoice",
          description: getQueryErrorMessage(err),
          variant: "destructive",
        });
      }
    } finally {
      setDocumentHeaderDownloading(false);
    }
  }, [detail, effectivePrintVariant, registrantPortalDocumentDownload]);

  const handleDownloadReceipt = useCallback(async () => {
    if (!receiptAnchor) {
      return;
    }
    setReceiptDownloading(true);
    try {
      const baseUrl = (client.getConfig().baseUrl || "").replace(/\/$/, "");
      const auth = client.getConfig().auth;
      const url = receiptAnchor.isInspection
        ? `${baseUrl}/shared/licenses/${receiptAnchor.licenseId}/inspection-payments/${receiptAnchor.paymentId}/receipt`
        : `${baseUrl}/shared/licenses/${receiptAnchor.licenseId}/payments/${receiptAnchor.paymentId}/receipt?scope=${receiptScopeAccess(userType).defaultScope}`;
      const doFetch =
        client.getConfig().fetch ?? globalThis.fetch.bind(globalThis);
      const response = await doFetch(
        new Request(url, {
          headers:
            typeof auth === "string" && auth
              ? { Authorization: `Bearer ${auth}` }
              : {},
        }),
      );
      if (!response.ok) {
        const text = await response.text();
        let detailMsg = text;
        try {
          const parsed = JSON.parse(text) as { detail?: string | string[] };
          if (typeof parsed.detail === "string") {
            detailMsg = parsed.detail;
          } else if (Array.isArray(parsed.detail)) {
            detailMsg = parsed.detail.join(", ");
          }
        } catch {
          // use raw text
        }
        throw new Error(detailMsg || response.statusText);
      }
      const blob = await response.blob();
      downloadFileFromResponse(
        blob,
        response.headers,
        receiptAnchor.isInspection
          ? `payment-receipt-inspection-${receiptAnchor.paymentId}.pdf`
          : `payment-receipt-${receiptAnchor.paymentId}.pdf`,
      );
    } catch (err) {
      toast({
        title: "Could not download receipt",
        description: getQueryErrorMessage(err),
        variant: "destructive",
      });
    } finally {
      setReceiptDownloading(false);
    }
  }, [receiptAnchor, userType]);

  const processingFeeDollarsByLicenseId: Record<string, number> = {};
  if (detail) {
    for (const lic of detail.licenses) {
      const sum = lic.payments.reduce(
        (acc, p) => acc + parseNum(p.processing_fee),
        0,
      );
      processingFeeDollarsByLicenseId[lic.license_id] = sum;
    }
  }

  const totals = detail
    ? (() => {
        const reg = parseNum(detail.total_registration_fee);
        const late = parseNum(detail.total_late_fee);
        const proc = parseNum(detail.total_processing_fee);
        let insp = 0;
        for (const lic of detail.licenses) {
          if (lic.inspection_payment) {
            insp += parseNum(lic.inspection_payment.fee_paid);
          }
        }
        return {
          registration_fee: reg,
          late_fee: late,
          inspection_fee: insp,
          processing_fee: proc,
          total: reg + late + insp + proc,
        };
      })()
    : null;

  const cancelNoun = effectivePrintVariant === "invoice" ? "invoice" : "check";

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full overflow-y-auto sm:max-w-2xl"
        >
          <SheetHeader className="space-y-0">
            <div className="flex flex-row flex-wrap items-start justify-between gap-4 pr-12">
              <SheetTitle>
                {detail?.payment_processor_transaction_id || activeProcessorId
                  ? "Payment details"
                  : printVariant === "invoice"
                    ? "Invoice details"
                    : "Check details"}
              </SheetTitle>
              <div className="flex flex-row flex-wrap items-center justify-end gap-2 shrink-0">
                {detail &&
                  !isLoading &&
                  !error &&
                  (headerUsesDocumentDownload ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={documentHeaderDownloading}
                      onClick={() => void handleDownloadDetailHeader()}
                    >
                      {documentHeaderDownloading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {headerDownloadGeneratingLabel}
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          {effectivePrintVariant === "invoice"
                            ? "Download Invoice"
                            : "Download"}
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        printManualPaymentDetail(detail, {
                          variant: effectivePrintVariant,
                        })
                      }
                    >
                      <Printer className="h-4 w-4 mr-2" />
                      Print
                    </Button>
                  ))}
                {registrantPortalDocumentDownload &&
                  registrantCancelEnabled &&
                  onRegistrantCancel &&
                  detail &&
                  !isLoading &&
                  !error && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={registrantCancelPending}
                      className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                      onClick={() => setConfirmCancelOpen(true)}
                    >
                      {registrantCancelPending ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Cancelling…
                        </>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 mr-2" />
                          Cancel...
                        </>
                      )}
                    </Button>
                  )}
              </div>
            </div>
          </SheetHeader>

          {!activeManualId && !activeProcessorId && null}
          {(activeManualId ?? activeProcessorId) && isLoading && (
            <div className="px-4 py-8 text-center text-muted-foreground">
              Loading...
            </div>
          )}
          {(activeManualId ?? activeProcessorId) && error && (
            <div className="px-4 py-4 text-destructive">
              {error instanceof Error ? error.message : "Failed to load"}
            </div>
          )}
          {detail && (
            <div className="space-y-6 px-4 py-4">
              <Card className="gap-4">
                <CardHeader className="flex flex-row items-start justify-between">
                  <CardTitle className="font-semibold text-xl">
                    {effectivePrintVariant === "invoice"
                      ? "Invoice"
                      : effectivePrintVariant === "processor"
                        ? "Payment"
                        : "Check"}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 grid-cols-2 items-start">
                    <div>
                      <p className="text-sm font-bold">
                        {effectivePrintVariant === "invoice"
                          ? "Invoice ID"
                          : effectivePrintVariant === "processor"
                            ? "Reference"
                            : "Check ID"}
                      </p>
                      <p className="text-sm">{detail.check_id}</p>
                    </div>
                    <div>
                      <p className="text-sm font-bold">Status</p>
                      <p className="text-sm">{detail.status}</p>
                    </div>
                    {detail.check_number &&
                      (effectivePrintVariant === "invoice" ? (
                        <div>
                          <p className="text-sm font-bold">Payment reference</p>
                          <p className="text-sm">{detail.check_number}</p>
                        </div>
                      ) : effectivePrintVariant !== "processor" ? (
                        <div>
                          <p className="text-sm font-bold">Check number</p>
                          <p className="text-sm">{detail.check_number}</p>
                        </div>
                      ) : null)}
                    {detail.note && (
                      <div>
                        <p className="text-sm font-bold">Payment note</p>
                        <p className="whitespace-pre-wrap text-sm">
                          {detail.note}
                        </p>
                      </div>
                    )}
                    {detail.registrant_name && (
                      <div>
                        <p className="text-sm font-bold">Registrant</p>
                        <p className="text-sm">{detail.registrant_name}</p>
                      </div>
                    )}
                    <div className="space-y-4">
                      {detail.registrant_company_name && (
                        <div>
                          <p className="text-sm font-bold">
                            Registrant Company
                          </p>
                          <p className="text-sm">
                            {detail.registrant_company_name}
                          </p>
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-bold">AMS</p>
                        <p className="text-sm">
                          {detail.ams_company_name?.trim()
                            ? detail.ams_company_name
                            : "—"}
                        </p>
                      </div>
                    </div>
                    {detail.payment_date && (
                      <div>
                        <p className="text-sm font-bold">Payment date</p>
                        <p className="text-sm">
                          {formatPaymentDocumentUsDate(detail.payment_date)}
                        </p>
                      </div>
                    )}
                    {detail.check_mailing_address && (
                      <div className="col-span-2">
                        <p className="text-sm font-bold">Mailing address</p>
                        <p className="text-sm whitespace-pre-wrap">
                          {detail.check_mailing_address}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="gap-4">
                <CardHeader className="flex flex-row items-start justify-between">
                  <CardTitle className="font-semibold text-xl">
                    Licenses
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {detail.licenses.map(
                    (lic: ManualCheckPaymentLicenseDetail) => (
                      <button
                        type="button"
                        key={lic.license_id}
                        className="block w-full cursor-pointer rounded-md text-left text-sm space-y-3 pb-3 mb-3 border-b border-border last:border-b-0 last:pb-0 last:mb-0 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => handleLicenseClick(lic.license_id)}
                        aria-label={`Open license ${lic.license_display_id}`}
                      >
                        <div className="grid gap-4 grid-cols-2 items-start space-y-0">
                          <div>
                            <p className="text-sm font-bold">ID</p>
                            <p className="text-sm text-primary underline underline-offset-2">
                              {lic.license_display_id}
                            </p>
                          </div>
                          {lic.property_address && (
                            <div>
                              <p className="text-sm font-bold">Address</p>
                              <p className="text-sm">{lic.property_address}</p>
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-bold">Jurisdiction</p>
                            <p className="text-sm">{lic.jurisdiction_name}</p>
                          </div>
                          <div>
                            <p className="text-sm font-bold">License Type</p>
                            <p className="text-sm">{lic.license_type_name}</p>
                          </div>
                        </div>
                        <div className="space-y-4">
                          {lic.payments.map((p, periodIndex) => {
                            const periodLabel = formatPaymentPeriodUsRangeLabel(
                              p.period_start_date,
                              p.period_end_date,
                            );
                            const showInspectionInThisPeriod =
                              periodIndex === 0 && lic.inspection_payment;
                            return (
                              <div key={p.id}>
                                <h4 className="font-bold text-sm mb-2">
                                  {periodLabel}
                                </h4>
                                <div className="border rounded-lg p-3 space-y-2">
                                  <div className="flex justify-between items-center text-sm">
                                    <span className="text-sm font-bold">
                                      Registration
                                    </span>
                                    <span>{formatCurrency(p.fee_paid)}</span>
                                  </div>
                                  <div className="flex justify-between items-center text-sm">
                                    <span className="text-sm font-bold">
                                      Late Fee
                                    </span>
                                    <span>
                                      {formatCurrency(p.late_fee_paid)}
                                    </span>
                                  </div>
                                  {showInspectionInThisPeriod && (
                                    <div className="flex justify-between items-center text-sm">
                                      <span className="text-sm font-bold">
                                        Inspection fee
                                      </span>
                                      <span>
                                        {formatCurrency(
                                          lic.inspection_payment?.fee_paid,
                                        )}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {(effectivePrintVariant !== "invoice" ||
                          (processingFeeDollarsByLicenseId[lic.license_id] ??
                            0) > 0) && (
                          <div className="flex justify-between items-center text-sm px-3">
                            <span className="text-sm font-bold">
                              Processing fee
                            </span>
                            <span>
                              {formatCurrency(
                                processingFeeDollarsByLicenseId[
                                  lic.license_id
                                ] ?? 0,
                              )}
                            </span>
                          </div>
                        )}
                      </button>
                    ),
                  )}
                </CardContent>
              </Card>

              {totals && (
                <Card className="gap-4">
                  <CardHeader className="flex flex-row items-start justify-between">
                    <CardTitle className="font-semibold text-xl">
                      Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between items-baseline text-sm">
                      <span className="text-sm font-bold">
                        Registration fees:
                      </span>
                      <span>{formatCurrency(totals.registration_fee)}</span>
                    </div>
                    <div className="flex justify-between items-baseline text-sm">
                      <span className="text-sm font-bold">Late fees:</span>
                      <span>{formatCurrency(totals.late_fee)}</span>
                    </div>
                    {totals.inspection_fee > 0 && (
                      <div className="flex justify-between items-baseline text-sm">
                        <span className="text-sm font-bold">
                          Inspection fees:
                        </span>
                        <span>{formatCurrency(totals.inspection_fee)}</span>
                      </div>
                    )}
                    {(effectivePrintVariant !== "invoice" ||
                      totals.processing_fee > 0) && (
                      <div className="flex justify-between items-baseline text-sm">
                        <span className="text-sm font-bold">
                          Processing fee:
                        </span>
                        <span>{formatCurrency(totals.processing_fee)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-baseline text-sm font-bold border-t pt-2 mt-2">
                      <span>Total:</span>
                      <span>{formatCurrency(totals.total)}</span>
                    </div>
                    {showReceiptForFinalisedCheck &&
                      !isReceiptTemplateLoading &&
                      hasPaymentReceiptTemplate && (
                        <div className="mt-4 pt-4 border-t">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => void handleDownloadReceipt()}
                            disabled={receiptDownloading}
                            aria-busy={receiptDownloading}
                          >
                            {receiptDownloading ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                {registrantPortalDocumentDownload
                                  ? "Generating Receipt"
                                  : "Downloading Receipt..."}
                              </>
                            ) : (
                              <>
                                <FileText className="h-4 w-4 mr-2" />
                                Download Receipt
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
      <ConfirmDeleteDialog
        isOpen={confirmCancelOpen}
        onOpenChange={setConfirmCancelOpen}
        description={`This will cancel this ${cancelNoun}.`}
        confirmLabel={
          effectivePrintVariant === "invoice"
            ? "Cancel Invoice"
            : "Cancel Check"
        }
        pending={registrantCancelPending}
        onConfirm={() => {
          setConfirmCancelOpen(false);
          onRegistrantCancel?.();
        }}
      />
    </>
  );
}
